import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Profile from './components/Profile.jsx';
import Teams from './components/Teams.jsx';
import Signature from './components/Signature';
import ProfileForm from './components/ProfileForm.jsx';
import Notifications from './components/Notifications.jsx';
import Password from './components/Password.jsx';
import NavBar from 'components/NavBar.jsx';
import './Detail.scss';
import Unauthorised from '../unauthorised/Unauthorised.jsx';
import GeckoPermissions from 'services/gecko-permissions';
import { GeckoRow, GeckoToastService } from 'ui';
import { selectAppStateLoggedInUser } from '../../reducers/appState_selector';
import { selectAgentTeams } from '../../reducers/agent_teams_selector';
import { renderError } from 'services/gecko-error';
import ManageAccount from './components/ManageAccount';
import GeckoRequest from 'services/gecko-request/index.js';

const navLinks = [
  {
    to: '/agents',
    activeClassName: 'active',
    linkText: 'Agents',
  },
  {
    to: '/teams',
    activeClassName: 'active',
    linkText: 'Teams',
  },
];

const Detail = ({ agent_id }) => {
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const loggedInUser = useSelector(selectAppStateLoggedInUser);
  const agents = useSelector((state) => state.agents);
  const groups = useSelector((state) => state.groups);
  const agent_teams = useSelector(selectAgentTeams);
  const teams = useSelector((state) => state.teams);
  const params = useSelector((state) => state.location.params);
  const defaultHeaders = useSelector((state) =>
    !state.appState.defaultHeaders
      ? state.appState.defaultHeaders
      : Object.keys(state.appState.defaultHeaders).reduce((headers, header) => {
          if (header === 'Content-Type') {
            return headers;
          }
          return Object.assign({}, headers, {
            [header]: state.appState.defaultHeaders[header],
          });
        }, {})
  );
  const uploadURL = useSelector(
    (state) => state.appState.apiUrl || process.env.REACT_APP_API_URL
  );
  const hasRadiusIntegration = useSelector((state) =>
    state.integrations.some((integration) => integration.type === 'radius')
  );
  const agent = agents.find((agent) => agent.id === agent_id);
  const type = groups.find((group) => group.id === agent.group_id).type;

  const uploadAvatar = (event) => {
    const [file] = event.target.files;
    const data = new FormData();

    if (!file) return;
    data.append('userId', agent.id);
    data.append('file', file);

    fetch(uploadURL + '/user/upload_avatar', {
      method: 'POST',
      headers: defaultHeaders,
      body: data,
    })
      .then((res) => {
        return res.json().then((data) => {
          if (res.status !== 200) {
            renderError(data);
          }
        });
      })
      .catch(renderError);
  };

  const deleteAvatar = async () => {
    GeckoRequest.delete('/user/avatar', { userId: agent.id }).catch(
      renderError
    );
  };

  const toggleAgentEditing = () => {
    setUpdatingAgent(!updatingAgent);
  };

  const getProfileOrForm = () => {
    let component;
    if (updatingAgent) {
      component = (
        <ProfileForm
          agent={agent}
          groups={groups}
          loggedInUser={loggedInUser}
          toggleAgentEditing={toggleAgentEditing}
          hasRadiusIntegration={hasRadiusIntegration}
        />
      );
    } else {
      component = (
        <Profile
          loggedInUser_id={loggedInUser.id}
          agent={agent}
          groups={groups}
          title="Agent Profile"
          uploadAvatar={uploadAvatar}
          deleteAvatar={deleteAvatar}
          toggleAgentEditing={toggleAgentEditing}
        />
      );
    }
    return component;
  };

  const renderAccountManagement = () => {
    const authUser = loggedInUser.migrated_to_auth;
    return authUser ? (
      <ManageAccount agent_id={agent_id} loggedInUser={loggedInUser} />
    ) : (
      <Password agent_id={agent_id} loggedInUser={loggedInUser} />
    );
  };

  if (type === 'admin' && !GeckoPermissions.memberOf('admin')) {
    return <Unauthorised />;
  }

  return (
    <div>
      <NavBar navLinks={navLinks} />
      <div className="content-margin">
        <GeckoRow>
          <div className="agent-details-left col-12 col-md-5">
            {getProfileOrForm()}
            <Signature agentId={agent.id} signature={agent.signature} />
            <Teams
              agent={agent}
              agents={agents}
              agent_teams={agent_teams}
              loggedInUser={loggedInUser}
              teams={teams}
              params={params}
            />
          </div>
          <div className="agent-details-right col-12 col-md-7">
            <Notifications agent={agent} />
            {renderAccountManagement()}
          </div>
        </GeckoRow>
      </div>
    </div>
  );
};

Detail.defaultProps = {};

Detail.propTypes = {
  agent_id: PropTypes.number.isRequired,
};

export default Detail;
